import type { AcceptanceCriterion } from '@/types';

interface ACListProps {
  items: AcceptanceCriterion[];
  checked: Record<string, boolean>;
  onToggle: (acId: string) => void;
}

// AC text is authored content (not user input). It may carry markdown bold
// (**x**) and inline HTML (<code>…</code>, &lt;/&gt; entities). The list used to
// render it as plain text, so all of that showed up raw. We escape stray `<`/`&`
// (so accidental raw `<` like "< Collection" in drafts can't break layout),
// keep whitelisted inline tags, then turn **bold** into <strong>.
const AC_AMP = /&(?!(?:lt|gt|amp|quot|#\d+|#x[0-9a-fA-F]+);)/g;
const AC_LT = /<(?!\/?(?:code|strong|em|b|br|span)\b)/g;
function renderAcText(raw: string): string {
  return raw
    .replace(AC_AMP, '&amp;')
    .replace(AC_LT, '&lt;')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

export function ACList({ items, checked, onToggle }: ACListProps) {
  const done = items.filter((i) => checked[i.id]).length;
  const pct = items.length === 0 ? 0 : Math.round((done / items.length) * 100);
  return (
    <>
      <div className="ac-progress-bar">
        <div className="bar">
          <div style={{ width: `${pct}%` }} />
        </div>
        <span>
          {done} / {items.length} · {pct}%
        </span>
      </div>
      <div className="ac-list">
        {items.map((item, idx) => {
          const isDone = !!checked[item.id];
          const onActivate = () => onToggle(item.id);
          return (
            <div
              key={item.id}
              className={`ac-item ${isDone ? 'done' : ''}`}
              role="checkbox"
              aria-checked={isDone}
              tabIndex={0}
              data-tour={idx === 0 ? 'ac-first-item' : undefined}
              onClick={onActivate}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActivate();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <span
                className="check"
                aria-hidden="true"
                role="presentation"
              />
              <div className="text" dangerouslySetInnerHTML={{ __html: renderAcText(item.text) }} />
            </div>
          );
        })}
      </div>
    </>
  );
}
